import { FC, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import AutorenewIcon from '@material-ui/icons/Autorenew';
import { Accordion, AccordionDetails, AccordionSummary, Box, Card,
  CardActions,
  CardContent,
  CardHeader, Divider, Grid, IconButton, styled, Table, TableBody,
  TableCell, TableHead, TableRow, TableSortLabel, Typography } from '@material-ui/core';
import { channelPartnerApi } from 'src/api/channelPartner';
import { getTimeStampFormatWithMeridiem } from 'src/utils/date-format';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { ActivityLog } from 'src/interfaces/common/IdValue';
import viewContext from 'src/viewContext';
import { ChevronRight as ChevronRightIcon } from '../../icons/chevron-right';
import { ChevronLeft as ChevronLeftIcon } from '../../icons/chevron-left';

interface ActivityHistoryLogProps {
  id: string;
}

export const ActivityHistoryLog: FC<ActivityHistoryLogProps> = (props) => {
  const { id } = props;
  const { mobileView } = useContext(viewContext);
  const [expanded, setExpanded] = useState<string | false>(false);
  const activityHisColumn = [
    {
      id: 'timestamp',
      label: 'DATE & TIME'
    },
    {
      id: 'activity',
      label: 'ACTIVITY'
    },
    {
      id: 'updatedBy',
      label: 'UPDATED BY'
    },
    {
      id: 'remark',
      label: 'REMARK',
      sortDisable: true
    },
  ];
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [sort, setSort] = useState('desc' as 'asc' | 'desc');
  const [sortBy, setSortBy] = useState('timestamp');
  const [pageNumber, setPageNumber] = useState(0);
  const pageSize = 5;

  const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const handleSortChange = (event, property: string) => {
    const isAsc = sortBy === property && sort === 'asc';
    if (!isAsc) {
      setActivityLogs(activityLogs.sort(
        // eslint-disable-next-line no-nested-ternary
        (a, b) => ((a[`${property}`] > b[`${property}`]) ? 1 : (a[`${property}`] === b[`${property}`]) ? ((a.timestamp > b.timestamp) ? 1 : -1) : -1)
      ));
      setSortBy(property);
      setSort('asc');
    } else {
      setActivityLogs(activityLogs.sort(
        // eslint-disable-next-line no-nested-ternary
        (a, b) => ((a[`${property}`] > b[`${property}`]) ? -1 : (a[`${property}`] === b[`${property}`]) ? ((a.timestamp > b.timestamp) ? -1 : 1) : 1)
      ));
      setSortBy(property);
      setSort('desc');
    }
  };

  const getTotalPagesCount = () => (Math.ceil(activityLogs?.length / pageSize));

  const getRows = () => {
    if (activityLogs?.length) {
      let size = (pageNumber * pageSize) + pageSize;
      if (pageNumber >= (getTotalPagesCount() - 1)) {
        size = activityLogs?.length;
      }
      const rows = [];
      for (let i = pageNumber * pageSize; i < size; i++) {
        if (activityLogs[i]?.id !== '') {
          const log = activityLogs[i];
          rows.push(mobileView
            ? (
              <Accordion
                expanded={expanded === `panel${i}`}
                onChange={handleChange(`panel${i}`)}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1bh-content"
                  id="panel1bh-header"
                  sx={{ backgroundColor: 'neutral.100' }}
                >
                  <Typography
                    variant="body2"
                  >
                    {log.activity}
                  </Typography>
                  <Box sx={{ flexGrow: 1 }} />
                  <Typography
                    variant="body2"
                  >
                    {log.updatedBy?.userName}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid
                    container
                  >
                    <Grid
                      xs={12}
                    >
                      <Typography
                        variant="subtitle2"
                      >
                        {getTimeStampFormatWithMeridiem(log?.timestamp)}
                      </Typography>
                    </Grid>
                    <Grid
                      xs={12}
                    >
                      <Typography
                        variant="body2"
                      >
                        {log.remark}
                      </Typography>
                    </Grid>
                    <Divider />
                  </Grid>
                </AccordionDetails>
              </Accordion>
            )
            : (
              <TableRow
                key={`${log?.timestamp}`}
              >
                <TableCell
                  width="25%"
                >
                  {getTimeStampFormatWithMeridiem(log?.timestamp)}
                </TableCell>
                <TableCell
                  width="10%"
                >
                  {log.activity}
                </TableCell>
                <TableCell
                  width="15%"
                >
                  {log.updatedBy?.userName}
                </TableCell>
                <TableCell
                  width="50%"
                >
                  {log.remark}
                </TableCell>
              </TableRow>
            ));
        }
      }
      return rows;
    }
    return [];
  };

  const PaginationRoot = styled('div')(
    (({ theme }) => ({
      alignItems: 'center',
      display: 'flex',
      padding: theme.spacing(2)
    }))
  );

  const handlePrev = () => {
    if (pageNumber === 0) return;
    setPageNumber(pageNumber - 1);
  };
  const handleNext = () => {
    setPageNumber(pageNumber + 1);
  };
  const getActivityHistoryDetails = async () => {
    const { responseData } = await channelPartnerApi.getActivityLogsById(id);
    // eslint-disable-next-line arrow-body-style
    const sortedActivity = responseData.sort((a, b) => {
      return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
    });
    setActivityLogs(sortedActivity);
  };

  useEffect(() => {
    getActivityHistoryDetails().catch(console.error);
  }, []);

  return (
    <>
      { id && (
      <Card
        variant="outlined"
        sx={{ mt: 1, pb: 0 }}
      >
        <CardHeader
          title="Activity History"
          action={(
            <>
              <IconButton
                sx={{ p: 0 }}
                title="Refresh Activity History"
                onClick={() => {
                  getActivityHistoryDetails();
                }}
              >
                <AutorenewIcon
                  color="primary"
                />
              </IconButton>
            </>
          )}
        />
        <CardContent
          sx={{
            p: { xs: 0, md: 2 }
          }}
        >
          <Grid
            container
            sx={{
              ml: { xs: 0 },
            }}
          >
            <Grid
              item
              md={12}
              xs={12}
              sx={{ pr: { xs: 0 } }}
            >
              <Box
                sx={{
                  mt: 0,
                  display: 'flex',
                  flex: 1,
                  flexDirection: 'column',
                  // border: 1,
                  borderColor: '#e1e3ea',
                  borderRadius: '2px',
                }}
              >
                <Box
                  sx={{
                    mt: 0,
                    // m: 1,
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'column',
                    border: 1,
                    borderColor: '#e1e3ea',
                    borderRadius: '2px',
                  }}
                >
                  { mobileView ? (
                    <>
                      {getRows()}
                    </>
                  ) : (
                    <Table
                      size="small"
                      sx={{
                        m: 0
                      }}
                    >
                      <TableHead>
                        <TableRow
                          key="activityHistory"
                        >
                          {activityHisColumn?.map((column) => (
                            <TableCell
                              key={`${column.id} ${new Date()}`}
                            >
                              <TableSortLabel
                                active={sortBy === column.id}
                                direction={sortBy === column.id ? sort : 'asc'}
                                hideSortIcon={column.sortDisable}
                                onClick={!column.sortDisable ? (
                                  event
                                ) => handleSortChange(event, column.id) : null}
                              >
                                {column.label}
                              </TableSortLabel>
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        { getRows()}
                      </TableBody>
                    </Table>
                  )}
                  <Divider sx={{ mt: 'auto' }} />
                  <PaginationRoot>
                    {getTotalPagesCount() > 0 && (
                    <Typography
                      color="textSecondary"
                      sx={{
                        alignItems: 'center',
                        display: 'flex'
                      }}
                      variant="body2"
                      whiteSpace="nowrap"
                    >
                      Page
                      {' '}
                      <Typography
                        color="textPrimary"
                        component="span"
                        sx={{ mx: 1 }}
                        variant="subtitle2"
                      >
                        {pageNumber + 1}
                      </Typography>
                      of
                      {' '}
                      <Typography
                        color="textPrimary"
                        component="span"
                        sx={{ ml: 1 }}
                        variant="subtitle2"
                      >
                        {getTotalPagesCount()}
                      </Typography>
                    </Typography>
                    )}
                    <Box sx={{ flexGrow: 1 }} />
                    <IconButton
                      disabled={pageNumber === 0}
                      onClick={handlePrev}
                    >
                      <ChevronLeftIcon />
                    </IconButton>
                    <IconButton
                      disabled={pageNumber >= (getTotalPagesCount() - 1)}
                      onClick={handleNext}
                    >
                      <ChevronRightIcon />
                    </IconButton>
                  </PaginationRoot>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
        <CardActions
          sx={{
            p: 0
          }}
        />
      </Card>
      )}
    </>
  );
};

ActivityHistoryLog.propTypes = {
  id: PropTypes.string
};
